/**
 * Video Progress API Service
 * Persists video watch progress per user in Cosmos DB via backend API.
 * Mirrors the interface of videoProgressService.js (localStorage version).
 *
 * API Base: {API_BASE_URL}/videoProgress
 * Auth: Bearer token via fetchWithAuth (MSAL)
 */

import fetchWithAuth from "../middlewares/fetchWithAuth";
import { API_BASE_URL } from "../config";

/**
 * @typedef {Object} VideoProgress
 * @property {number} viewCount - Number of times this video has been watched past threshold
 * @property {number} secondsWatched - Total seconds watched (forward playback only)
 * @property {string|null} lastWatchedAt - ISO timestamp of last update
 * @property {boolean} watched - True if video has been watched past threshold
 */

const VIDEO_PROGRESS_URL = `${API_BASE_URL}/videoProgress`;

/**
 * Authenticated fetch helper for video progress endpoints.
 * @param {string} url
 * @param {RequestInit} options
 * @param {Function} navigate
 * @returns {Promise<Response>}
 */
async function authFetch(url, options = {}, navigate) {
  const response = await fetchWithAuth(
    url,
    {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
    },
    navigate
  );

  if (!response.ok) {
    throw new Error(`Video progress API error: ${response.status}`);
  }

  return response;
}

/**
 * Get progress for a single video from the API.
 * @param {string|number} videoId
 * @param {Function} navigate - React Router navigate function
 * @returns {Promise<VideoProgress|null>}
 */
export async function getVideoProgress(videoId, navigate) {
  if (videoId == null || videoId === '') return null;
  try {
    const response = await authFetch(
      `${VIDEO_PROGRESS_URL}/${encodeURIComponent(String(videoId))}`,
      { method: "GET" },
      navigate
    );
    const data = await response.json();
    return data || null;
  } catch (error) {
    if (error.message?.includes("404")) return null;
    throw error;
  }
}

/**
 * Get progress for all videos for the authenticated user.
 * @param {Function} navigate
 * @returns {Promise<Object.<string, VideoProgress>>}
 */
export async function getAllProgress(navigate) {
  try {
    const response = await authFetch(
      `${VIDEO_PROGRESS_URL}/`,
      { method: "GET" },
      navigate
    );
    const data = await response.json();
    return data || {};
  } catch {
    return {};
  }
}

/**
 * Upsert video progress via the API.
 * @param {string|number} videoId
 * @param {Object} options
 * @param {number} options.secondsWatched - Total seconds watched so far
 * @param {number} [options.videoDuration] - Video duration in seconds
 * @param {boolean} [options.countNewView] - Whether to increment view count
 * @param {Function} navigate
 * @returns {Promise<VideoProgress|null>}
 */
export async function updateVideoProgress(videoId, { secondsWatched, videoDuration, countNewView }, navigate) {
  if (videoId == null || videoId === '') return null;
  const response = await authFetch(
    `${VIDEO_PROGRESS_URL}/${encodeURIComponent(String(videoId))}`,
    {
      method: "PUT",
      body: JSON.stringify({ secondsWatched, videoDuration, countNewView }),
    },
    navigate
  );
  return response.json();
}

/**
 * Reset progress for a single video.
 * @param {string|number} videoId
 * @param {Function} navigate
 * @returns {Promise<VideoProgress|null>}
 */
export async function resetVideoProgress(videoId, navigate) {
  if (videoId == null || videoId === '') return null;
  const response = await authFetch(
    `${VIDEO_PROGRESS_URL}/${encodeURIComponent(String(videoId))}`,
    { method: "DELETE" },
    navigate
  );
  return response.json();
}

/**
 * Bulk upsert all progress for the authenticated user (used for localStorage → API sync).
 * Sends the entire progress map to be merged server-side.
 * @param {Object.<string, VideoProgress>} progressMap
 * @param {Function} navigate
 * @returns {Promise<void>}
 */
export async function bulkUpsertProgress(progressMap, navigate) {
  if (!progressMap || Object.keys(progressMap).length === 0) return;
  await authFetch(
    `${VIDEO_PROGRESS_URL}/bulk`,
    {
      method: "PUT",
      body: JSON.stringify(progressMap),
    },
    navigate
  );
}
